/** @format */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
	UserPlus,
	AlertCircle,
	KeyRound,
	Mail,
	User,
	Loader2,
	Eye,
	EyeOff,
	Lock,
	Check,
	X,
} from "lucide-react"; // Added Eye/EyeOff, Lock, Check, X

// Actions & Components
import { register, clearError } from "../actions/userActions";
import { FormInput } from "../components/FormInput"; // Assuming path
import { LoadingSpinner } from "../components/LoadingSpinner"; // Assuming path

// --- Reusable Error Message Component ---
const ErrorMessage = ({ message } /* ... same as before ... */) => (
	<motion.div
		initial={{ opacity: 0, y: -10 }}
		animate={{ opacity: 1, y: 0 }}
		exit={{ opacity: 0 }}
		role="alert"
		aria-live="assertive"
		className="bg-red-50 border border-red-300 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2 mt-1 mb-4"
	>
		<AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-500" />{" "}
		<p>{message}</p>
	</motion.div>
);

// --- Password Strength/Requirements Indicator ---
const PasswordRequirements = ({ password = "" }) => {
	const checks = useMemo(
		() => [
			{ label: "At least 6 characters", pattern: /.{6,}/ },
			{ label: "Contains a number", pattern: /[0-9]/ },
			// Add more checks if desired (e.g., uppercase, symbol)
			// { label: "Contains an uppercase letter", pattern: /[A-Z]/ },
			// { label: "Contains a special character", pattern: /[!@#$%^&*]/ },
		],
		[]
	);

	const fulfilled = useMemo(
		() => checks.map((check) => check.pattern.test(password)),
		[password, checks]
	);
	const allFulfilled = fulfilled.every(Boolean);

	// Don't show if password is empty
	if (!password) return null;

	return (
		<motion.div
			initial={{ opacity: 0, height: 0 }}
			animate={{ opacity: 1, height: "auto" }}
			exit={{ opacity: 0, height: 0 }}
			className="text-xs space-y-1 mt-2 pl-1"
		>
			{checks.map((check, index) => (
				<div
					key={index}
					className={`flex items-center gap-1.5 transition-colors ${
						fulfilled[index] ? "text-green-600" : "text-gray-500"
					}`}
				>
					{fulfilled[index] ? (
						<Check size={12} strokeWidth={3} />
					) : (
						<X size={12} strokeWidth={3} />
					)}
					<span>{check.label}</span>
				</div>
			))}
		</motion.div>
	);
};

// --- Main Register Screen Component ---
const RegisterScreen = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// --- State ---
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		password: "",
		confirmPassword: "",
	});
	const [formErrors, setFormErrors] = useState({
		password: "",
		confirmPassword: "",
	}); // Client-side validation errors
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	// --- Redux State ---
	// Clearer state naming
	const {
		loading: isLoading,
		error: submissionError,
		userInfo,
		isGuest,
	} = useSelector((state) => state.userLogin);

	// --- URL Params ---
	const [searchParams] = useSearchParams();
	const redirectPath = useMemo(
		() => searchParams.get("redirect") || "/",
		[searchParams]
	);

	// --- Effects ---
	// Clear submission errors when component mounts or unmounts
	useEffect(() => {
		dispatch(clearError()); // Clear previous errors on mount
		return () => dispatch(clearError()); // Clear on unmount
	}, [dispatch]);

	// Redirect if already logged in
	useEffect(() => {
		if (userInfo && !isGuest) {
			console.log(
				"User logged in, redirecting from Register to:",
				redirectPath
			);
			navigate(redirectPath, { replace: true });
		}
	}, [navigate, userInfo, isGuest, redirectPath]);

	// --- Validation ---
	// Password validation logic
	const validatePassword = useCallback((password) => {
		if (!password) return ""; // Don't show error if empty yet
		if (password.length < 6) return "Password must be at least 6 characters.";
		if (!/[0-9]/.test(password))
			return "Password must contain at least one number.";
		// Add more validation rules here if needed
		return ""; // No error
	}, []);

	// Real-time validation on input change
	const handleInputChange = useCallback(
		(e) => {
			const { name, value } = e.target;
			setFormData((prev) => ({ ...prev, [name]: value }));

			// --- Client-side Validation ---
			let passwordError = formErrors.password;
			let confirmPasswordError = formErrors.confirmPassword;

			if (name === "password") {
				passwordError = validatePassword(value);
				// Re-check confirm password if password changes
				confirmPasswordError =
					formData.confirmPassword && value !== formData.confirmPassword
						? "Passwords do not match."
						: "";
			}

			if (name === "confirmPassword") {
				confirmPasswordError =
					value && value !== formData.password ? "Passwords do not match." : "";
			}

			setFormErrors({
				password: passwordError,
				confirmPassword: confirmPasswordError,
			});
		},
		[
			formData.password,
			formData.confirmPassword,
			validatePassword,
			formErrors.password,
			formErrors.confirmPassword,
		]
	); // Include formErrors in deps? Careful with loops

	// --- Form Submission ---
	const handleSubmit = useCallback(
		(e) => {
			e.preventDefault();

			// Trigger final validation check
			const finalPasswordError = validatePassword(formData.password);
			const finalConfirmError =
				formData.password !== formData.confirmPassword
					? "Passwords do not match."
					: "";

			setFormErrors({
				password: finalPasswordError,
				confirmPassword: finalConfirmError,
			});

			// Check if any client-side errors exist
			if (
				finalPasswordError ||
				finalConfirmError ||
				!formData.name ||
				!formData.email
			) {
				console.warn(
					"Registration submission blocked by client-side validation."
				);
				return; // Don't dispatch if errors found
			}

			// If valid, dispatch registration action
			dispatch(register(formData.name, formData.email, formData.password));
		},
		[dispatch, formData, validatePassword]
	); // Dependencies

	// Determine specific error message for display (e.g., user exists)
	const specificSubmissionError = useMemo(() => {
		if (!submissionError) return null;
		// Check for specific backend messages
		if (
			submissionError.toLowerCase().includes("email already exists") ||
			submissionError.includes("401") ||
			submissionError.includes("duplicate")
		) {
			return "An account with this email already exists. Please try logging in or reset your password.";
		}
		// Generic error
		return submissionError;
	}, [submissionError]);

	return (
		<div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.3, ease: "easeOut" }}
				className="max-w-md w-full space-y-8"
			>
				{/* --- Header --- */}
				<div className="text-center">
					{/* <img className="mx-auto h-12 w-auto" src="/images/logoDark-1.png" alt="Off Cut Shades"/> */}
					<motion.h2
						initial={{ y: -10 }}
						animate={{ y: 0 }}
						transition={{ delay: 0.1 }}
						className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900"
					>
						Create Your Account
					</motion.h2>
					<motion.p
						initial={{ y: -10 }}
						animate={{ y: 0 }}
						transition={{ delay: 0.15 }}
						className="mt-2 text-center text-sm text-gray-600"
					>
						Get access to exclusive offers and faster checkout.
					</motion.p>
				</div>

				{/* --- Form Card --- */}
				<div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
					{/* Display Submission Error */}
					<AnimatePresence>
						{specificSubmissionError && (
							<ErrorMessage message={specificSubmissionError} />
						)}
					</AnimatePresence>

					<form onSubmit={handleSubmit} className="space-y-4">
						{" "}
						{/* Reduced space */}
						<FormInput
							label="Full Name"
							name="name"
							type="text"
							id="full-name"
							icon={<User />}
							required
							value={formData.name}
							onChange={handleInputChange}
							placeholder="e.g., Jane Doe"
							autoComplete="name"
							disabled={isLoading}
						/>
						<FormInput
							label="Email Address"
							name="email"
							type="email"
							id="email-address-register"
							icon={<Mail />}
							required
							value={formData.email}
							onChange={handleInputChange}
							placeholder="you@example.com"
							autoComplete="email"
							disabled={isLoading}
						/>
						<div>
							<FormInput
								label="Password"
								name="password"
								type={showPassword ? "text" : "password"}
								id="password-register"
								icon={<KeyRound />}
								required
								value={formData.password}
								onChange={handleInputChange}
								error={formErrors.password} // Show client-side validation error
								placeholder="Create a password"
								autoComplete="new-password"
								disabled={isLoading}
								// Optional: Add Eye icon button within FormInput or absolutely positioned here
								// E.g., addonRight={<button type="button" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff/> : <Eye/>}</button>}
							/>
							{/* Password Requirements Component */}
							<PasswordRequirements password={formData.password} />
						</div>
						<div>
							<FormInput
								label="Confirm Password"
								name="confirmPassword"
								type={showConfirmPassword ? "text" : "password"}
								id="confirm-password"
								icon={<KeyRound />}
								required
								value={formData.confirmPassword}
								onChange={handleInputChange}
								error={formErrors.confirmPassword} // Show client-side validation error
								placeholder="Confirm your password"
								autoComplete="new-password"
								disabled={isLoading}
								// Optional: Add Eye icon button
							/>
						</div>
						<motion.button
							whileTap={{ scale: 0.98 }}
							type="submit"
							disabled={
								isLoading ||
								!!formErrors.password ||
								!!formErrors.confirmPassword
							} // Disable if loading or client errors
							className="w-full btn btn-primary py-3 text-base mt-6" // Added margin top
						>
							{isLoading ? (
								<>
									<Loader2 className="w-5 h-5 animate-spin" /> Creating
									Account...
								</>
							) : (
								<>
									<UserPlus className="w-5 h-5" /> Create Account
								</>
							)}
						</motion.button>
					</form>

					{/* --- Separator & Login Link --- */}
					<div className="mt-6 pt-6 border-t border-gray-200">
						<p className="text-center text-sm text-gray-600">
							Already have an account?
						</p>
						<Link
							// Preserve redirect param if user decides to log in instead
							to={
								redirectPath && redirectPath !== "/"
									? `/login?redirect=${encodeURIComponent(redirectPath)}`
									: "/login"
							}
							className="mt-3 w-full inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors gap-2"
						>
							Sign In Instead
						</Link>
						<p className="mt-4 text-xs text-gray-500 text-center flex items-center justify-center gap-1">
							<Lock size={12} /> We value your privacy.
						</p>
					</div>
				</div>
			</motion.div>

			{/* Removed separate Loading Overlay - incorporated into button */}
		</div>
	);
};

export default RegisterScreen;
